import React, { createContext, useContext } from 'react'
import {
  createOrGetVisitSessionIdFromCookie,
  getVisitSessionId,
} from '@mc/track-event'

/**
 * Injectable source for the visit-session lifecycle.
 *
 * Defaults to the cookie path used by web (core/next). Electron injects its own
 * source because `document.cookie` is inert under `file://`, which otherwise
 * churns the whole session trio every render (DAIS-5925).
 */
export interface VisitSessionSource {
  /** Current visit id, or `undefined` when none is established yet. */
  getVisitSessionId: () => string | undefined
  /** Returns the current visit id, creating one if absent. */
  createOrGetVisitSessionId: () => string
  /** Fired on each media event so a source can keep the visit alive. */
  onMediaActivity?: () => void
}

const defaultVisitSessionSource: VisitSessionSource = {
  getVisitSessionId,
  createOrGetVisitSessionId: createOrGetVisitSessionIdFromCookie,
}

const VisitSessionSourceContext = createContext<VisitSessionSource>(
  defaultVisitSessionSource,
)

export const VisitSessionSourceProvider = ({
  value,
  children,
}: {
  value: VisitSessionSource
  children?: React.ReactNode
}) => (
  <VisitSessionSourceContext.Provider value={value}>
    {children}
  </VisitSessionSourceContext.Provider>
)

export const useVisitSessionSource = () => useContext(VisitSessionSourceContext)
